import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { routeTree } from "./routeTree.gen";
import "./styles/globals.css";

/**
 * The SPA entry point.
 *
 * TanStack Query owns server state; TanStack Router owns where you are. The
 * router carries the query client on its context so route guards can read the
 * session before a component mounts — that is what makes the redirect to
 * /login happen instead of a page flashing and then bouncing.
 */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // The API is on the same origin and cheap to re-ask; what is expensive
      // is a founder seeing a spinner for data that has not changed.
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  // Prefetch on hover: the loop is a small set of pages she moves between
  // constantly, and the whole app is already downloaded.
  defaultPreload: "intent",
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const el = document.getElementById("root");
if (!el) throw new Error("#root missing from index.html");

createRoot(el).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
